import { createTheme } from "@mui/material/styles";
import {
  DEFAULT_INK,
  DEFAULT_TOKENS as D,
  brandingKey,
  buildReplacer,
  deriveBrandPalette,
  recolorTree,
  snapshotColorTree,
} from "./brandPalette.js";

// Marca Markezy: rosa que migra a naranja/amarillo (mismo gradiente que el login).
// Fuente única de verdad para el color primario; el resto del theme deriva de aquí.
const brand = {
  pink: D.pink,
  pinkDark: D.pinkDark,
  coral: D.coral,
  orange: D.orange, // Secundario de la agencia (naranja por defecto): series de comparación en gráficas
  orangeText: DEFAULT_INK, // Texto legible sobre `orange`
  soft: D.soft, // Fondo rosa suave (item activo del menú, chips)
  softBorder: D.softBorder,
  softText: D.pinkDark,
  ink: DEFAULT_INK, // Negro de marca como color de texto
  inkFill: DEFAULT_INK, // Fondo de botones oscuros y círculos de stepper (secundario de la agencia)
  inkFillText: "#FFFFFF",
  inkHover: "#1E293B",
  avatar: DEFAULT_INK, // Fondo de avatares de iniciales
  mediaSurface: "linear-gradient(135deg, #FBEFF5 0%, #FCF3EC 100%)", // Zona de subir imagen
  mediaBorder: "#E3B8CF",
  gradient: `linear-gradient(95deg, ${D.pink} 0%, ${D.orange} 100%)`,
  gradientHover: `linear-gradient(95deg, ${D.pinkDark} 0%, #E0850B 100%)`,
  gradientDiagonal: `linear-gradient(135deg, ${D.pink} 0%, ${D.coral} 55%, ${D.orange} 100%)`,
  shadow: `0 4px 14px rgba(${D.rgb}, 0.28)`,
  shadowHover: `0 6px 18px rgba(${D.rgb}, 0.36)`,
};

// Colores base del sistema
const colors = {
  brand,

  // Menú lateral: texto, iconos y títulos de sección
  nav: {
    text: "#1A2C44",
    icon: "#3C4F69",
    section: "#3C4F69",
  },

  // Colores primarios
  primary: {
    main: brand.pink, // Rosa principal
    light: brand.coral,
    dark: brand.pinkDark,
    extralight: brand.soft,
    contrastText: "#ffffff",
  },

  // Colores secundarios
  secondary: {
    main: "#f1f5f9", // Gris claro
    light: "#f8fafc",
    dark: "#e2e8f0",
    contrastText: "#0f172a",
  },

  // Estados de error/destructivo
  error: {
    main: "#ef4444", // Rojo destructivo
    light: "#fca5a5",
    dark: "#dc2626",
    contrastText: "#ffffff",
  },

  // Estados de advertencia
  warning: {
    main: "#f59e0b",
    light: "#fbbf24",
    dark: "#d97706",
    contrastText: "#ffffff",
  },

  // Estados de éxito
  success: {
    main: "#0F9D58",
    light: "#4CC38A",
    dark: "#0F9D58",
    contrastText: "#ffffff",
  },

  // Colores del success popup
  successPopup: {
    success: {
      background: "#0F9D58",
      text: "#ffffff",
      icon: "#111827",
    },
    error: {
      background: "#FF3B30",
      text: "#ffffff",
      icon: "#111827",
    },
  },

  // Estados de información
  info: {
    main: brand.pink,
    light: brand.coral,
    dark: brand.pinkDark,
    contrastText: "#ffffff",
    main_blue: "#3b82f6", // Blue
    light_blue: "#93c5fd",
    dark_blue: "#1d4ed8",
  },

  // Link
  link: "#2563eb",

  // Botones de tutorial
  tutorial: {
    main: "#8b5cf6",
    light: "#ddd6fe",
    dark: "#7c3aed",
    contrastText: "#ffffff",
    border: "#c4b5fd",
  },

  // Colores específicos para mensajes
  message: {
    own: brand.soft, // Fondo para mensajes propios
  },

  // Colores para estados de tareas
  taskCompleted: {
    background: "#f3f4f6",
    text: "#1f2937",
    border: "#e5e7eb",
  },

  // Tabla clientify
  clientifyTable: {
    statusBadges: {
      hotLead: {
        background: "#fee2e2",
        text: "#991b1b",
        border: "#fecaca",
      },
      coldLead: {
        background: "#e0f2fe",
        text: "#075985",
        border: "#bae6fd",
      },
      lostClient: {
        background: "#f3f4f6",
        text: "#374151",
        border: "#e5e7eb",
      },
      inDeal: {
        background: "#E3F6EA",
        text: "#0F9D58",
        border: "#C4E8D2",
      },
      lostLead: {
        background: "#f3f4f6",
        text: "#374151",
        border: "#e5e7eb",
      },
      client: {
        background: "#f3e8ff",
        text: "#6b21a8",
        border: "#e9d5ff",
      },
      warmLead: {
        background: "#fef3c7",
        text: "#92400e",
        border: "#fde68a",
      },
      default: {
        background: "transparent",
        text: "#334155",
        border: "#cbd5e1",
      },
    },
  },

  // Gradientes usados para accesos rápidos de la zona SERSEO
  quickLinks: {
    academy: {
      start: "#a855f7",
      end: "#ec4899",
      text: "#ffffff",
      iconBackground: "rgba(255, 255, 255, 0.2)",
    },
    helpdesk: {
      start: "#6366f1",
      end: "#7c3aed",
      text: "#ffffff",
      iconBackground: "rgba(255, 255, 255, 0.18)",
    },
    webmail: {
      start: "#06b6d4",
      end: "#3b82f6",
      text: "#ffffff",
      iconBackground: "rgba(255, 255, 255, 0.2)",
    },
    meetings: {
      start: "#f472b6",
      end: "#c084fc",
      text: "#ffffff",
      iconBackground: "rgba(255, 255, 255, 0.22)",
    },
    kitdigital: {
      start: "#ec4899",
      end: "#ef4444",
      text: "#ffffff",
      iconBackground: "rgba(255, 255, 255, 0.2)",
    },
    chatwith: {
      start: "#fb923c",
      end: "#f97316",
      text: "#ffffff",
      iconBackground: "rgba(255, 255, 255, 0.2)",
    },
    otr: {
      start: "#fbbf24",
      end: "#f59e0b",
      text: "#ffffff",
      iconBackground: "rgba(255, 255, 255, 0.2)",
    },
  },

  // Gradiente para la página de login
  loginGradient: brand.gradient,

  // Gradiente para el badge de beta
  betaBadgeGradient: brand.gradient,
};
const neutrals = {
  white: "#ffffff",
  black: "#000000",
  gray: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },
};

const notificationTypeColors = {
  unread_in_thread: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  ticket_created: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  ticket_assigned: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  ticket_status_changed: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  ticket_hours_redeemed: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  user_created: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  client_info_updated: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  client_created: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  client_onboarding_completed: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  central_announcement: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  conversation_task_assigned: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  task_assigned: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  conversation_important: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  conversation_mention: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
  default: {
    text: colors.primary.main,
    dot: colors.primary.main,
    background: brand.soft,
  },
};

export const changeTypeColors = {
  initial: "#0F9D58",
  manual_edit: "#3b82f6",
  ai_iteration: "#8b5cf6",
  client_edit: "#ef4444", // Red color for client edits
};

// Paleta de marca según rol. Tras el rebranding todos los roles comparten
// la misma marca; se mantiene el mapa por rol para no romper consumidores.
const brandRolePalette = {
  primary: brand.pink,
  light: brand.coral,
  dark: brand.pinkDark,
  contrastText: "#ffffff",
  gradient: brand.gradient,
  pastelBackground: brand.soft,
  pastelBorder: brand.softBorder,
  pastelText: brand.softText,
  shadow: "rgba(230, 0, 126, 0.25)",
  ripple: "rgba(230, 0, 126, 0.2)",
};

const roleBranding = {
  superadmin: brandRolePalette,
  agency_admin: brandRolePalette,
  consultant: brandRolePalette,
  freelance_ticket: brandRolePalette,
  client: brandRolePalette,
};

roleBranding.default = roleBranding.superadmin;

// Configuración del theme claro
const lightTheme = {
  palette: {
    mode: "light",
    primary: colors.primary,
    secondary: colors.secondary,
    error: colors.error,
    warning: colors.warning,
    success: colors.success,
    info: colors.info,
    background: {
      default: neutrals.white,
      paper: neutrals.white,
      card: neutrals.white,
      accent: neutrals.gray[50],
      muted: neutrals.gray[100],
    },
    text: {
      primary: neutrals.gray[900],
      secondary: neutrals.gray[600],
      muted: neutrals.gray[500],
      disabled: neutrals.gray[400],
    },
    divider: neutrals.gray[200],
    border: {
      default: neutrals.gray[200],
      input: neutrals.gray[300],
      muted: neutrals.gray[100],
    },
    action: {
      hover: neutrals.gray[50],
      selected: neutrals.gray[100],
      disabled: neutrals.gray[300],
      disabledBackground: neutrals.gray[100],
    },
  },
};

// Configuración del theme oscuro (preparado para futuro)
const darkTheme = {
  palette: {
    mode: "dark",
    primary: {
      ...colors.primary,
      main: brand.coral, // Rosa más claro para modo oscuro
    },
    secondary: {
      main: neutrals.gray[800],
      light: neutrals.gray[700],
      dark: neutrals.gray[900],
      contrastText: neutrals.white,
    },
    error: colors.error,
    warning: colors.warning,
    success: colors.success,
    info: colors.info,
    background: {
      default: neutrals.gray[900],
      paper: neutrals.gray[800],
      card: neutrals.gray[800],
      accent: neutrals.gray[800],
      muted: neutrals.gray[700],
    },
    text: {
      primary: neutrals.white,
      secondary: neutrals.gray[300],
      muted: neutrals.gray[400],
      disabled: neutrals.gray[500],
    },
    divider: neutrals.gray[700],
    border: {
      default: neutrals.gray[700],
      input: neutrals.gray[600],
      muted: neutrals.gray[800],
    },
    action: {
      hover: neutrals.gray[700],
      selected: neutrals.gray[700],
      disabled: neutrals.gray[600],
      disabledBackground: neutrals.gray[800],
    },
  },
};

// Tipografía base
const typography = {
  fontFamily: [
    "-apple-system",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    "Roboto",
    '"Helvetica Neue"',
    "Arial",
    "sans-serif",
  ].join(","),
  h1: {
    fontSize: "2.25rem", // 36px
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h2: {
    fontSize: "1.875rem", // 30px
    fontWeight: 500,
    lineHeight: 1.3,
  },
  h3: {
    fontSize: "1.5rem", // 24px
    fontWeight: 500,
    lineHeight: 1.3,
  },
  h4: {
    fontSize: "1.25rem", // 20px
    fontWeight: 500,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: "1.125rem", // 18px
    fontWeight: 500,
    lineHeight: 1.4,
  },
  h6: {
    fontSize: "1rem", // 16px
    fontWeight: 500,
    lineHeight: 1.4,
  },
  body1: {
    fontSize: "1rem", // 16px
    fontWeight: 400,
    lineHeight: 1.5,
  },
  body2: {
    fontSize: "0.875rem", // 14px
    fontWeight: 400,
    lineHeight: 1.5,
  },
  caption: {
    fontSize: "0.75rem", // 12px
    fontWeight: 400,
    lineHeight: 1.4,
  },
  button: {
    fontSize: "0.875rem", // 14px
    fontWeight: 500,
    lineHeight: 1.4,
    textTransform: "none", // No uppercase automático
  },
};

// Espaciado base
const spacing = 8; // 8px base unit

// Bordes redondeados eliminados - usar estilos inline

// Sombras (MUI requiere al menos 25 niveles para compatibilidad con elevation)
const shadows = [
  "none",
  "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
];

// Función para crear el theme basado en el modo
export const createAppTheme = (mode = "light") => {
  const themeConfig = mode === "light" ? lightTheme : darkTheme;

  return createTheme({
    palette: themeConfig.palette,
    typography,
    spacing,
    shape: {
      borderRadius: 8,
    },
    shadows,
    components: {
      // Personalización global de componentes MUI
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          },
          // Botón principal de marca: píldora con gradiente rosa → naranja
          containedPrimary: {
            background: brand.gradient,
            color: colors.primary.contrastText,
            boxShadow: brand.shadow,
            "&:hover": {
              background: brand.gradientHover,
              boxShadow: brand.shadowHover,
            },
            "&.Mui-disabled": {
              background: neutrals.gray[200],
              color: neutrals.gray[400],
              boxShadow: "none",
            },
          },
          outlinedPrimary: {
            borderColor: brand.softBorder,
            "&:hover": {
              borderColor: brand.pink,
              backgroundColor: brand.soft,
            },
          },
          textPrimary: {
            "&:hover": {
              backgroundColor: brand.soft,
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          barColorPrimary: {
            background: brand.gradient,
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          primary: {
            background: brand.gradient,
            boxShadow: brand.shadow,
            "&:hover": {
              background: brand.gradientHover,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: shadows[1],
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 8,
            },
          },
        },
      },
      // Foco de inputs/selectores en negro en vez del color de marca.
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: neutrals.black,
            },
            "&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.error.main,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: neutrals.black,
            },
            "&.Mui-focused.Mui-error": {
              color: colors.error.main,
            },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          underline: {
            "&:after": {
              borderBottomColor: neutrals.black,
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: (theme) => ({
          "*::-webkit-scrollbar": {
            width: "6px",
          },
          "*::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.divider,
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: theme.palette.text.secondary,
            },
          },
          "*": {
            scrollbarWidth: "thin",
            scrollbarColor: `${theme.palette.divider} transparent`,
          },
          "html::-webkit-scrollbar, body::-webkit-scrollbar": {
            width: "12px",
          },
          "html::-webkit-scrollbar-track, body::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "html::-webkit-scrollbar-thumb, body::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.divider,
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: theme.palette.text.secondary,
            },
          },
          "html, body": {
            scrollbarWidth: "auto",
            scrollbarColor: `${theme.palette.divider} transparent`,
          },
        }),
      },
    },
    // Variables personalizadas para uso en sx={}
    custom: {
      colors,
      neutrals,
      roleBranding,
      roleBadges: {
        agency_admin: {
          backgroundColor: "#fef3c7", // yellow (TeamMemberCard)
          color: "#92400e",
          borderColor: "#fde68a",
        },
        consultant: {
          backgroundColor: "#dbeafe", // blue (TeamMemberCard)
          color: "#1e40af",
          borderColor: "#bfdbfe",
        },
        freelance_ticket: {
          backgroundColor: "#fff7ed",
          color: "#9a3412",
          borderColor: "#fdba74",
        },
        client: {
          backgroundColor: neutrals.white,
          color: neutrals.gray[700],
          borderColor: neutrals.gray[200],
        },
        superadmin: {
          backgroundColor: brand.soft,
          color: brand.softText,
          borderColor: brand.softBorder,
        },
        default: {
          backgroundColor: neutrals.gray[100],
          color: neutrals.gray[800],
          borderColor: neutrals.gray[200],
        },
      },
      notificationTypeColors,
      changeTypeColors,
      platformColors,
      spacing: {
        xs: spacing * 0.5, // 4px
        sm: spacing * 1, // 8px
        md: spacing * 2, // 16px
        lg: spacing * 3, // 24px
        xl: spacing * 4, // 32px
        "2xl": spacing * 6, // 48px
        "3xl": spacing * 8, // 64px
      },
    },
  });
};

// Theme por defecto (modo claro); lo crea applyBranding al final del módulo y se
// regenera al aplicar la marca de una agencia
export let theme;

// Colores de plataformas sociales
export const platformColors = {
  linkedin: "#0A66C2",
  instagram: "#E4405F",
  facebook: "#1877F2",
  youtube: "#FF0000",
  tiktok: "#000000",
  google_business_profile: "#4285F4",
  blog: "#333333",
  email_marketing: "#64748b",
  others: brand.pink,
};

// Variables de colores específicas para badges y estados
export const badgeColors = {
  pack: {
    ecommerce: {
      background: "#f3e8ff",
      text: "#6b21a8",
      border: "#e9d5ff",
    },
    basic: {
      background: "#dbeafe",
      text: "#1e40af",
      border: "#bfdbfe",
    },
    premium: {
      background: "#fef3c7",
      text: "#92400e",
      border: "#fde68a",
    },
    local: {
      background: "#E3F6EA",
      text: "#0F9D58",
      border: "#C4E8D2",
    },
  },
  status: {
    active: {
      background: "#E3F6EA",
      text: "#0F9D58",
      border: "#a7f3d0",
    },
    inactive: {
      background: "#f3f4f6",
      text: "#1f2937",
      border: "#e5e7eb",
    },
    pending: {
      background: "#fed7aa",
      text: "#9a3412",
      border: "#fdba74",
    },
  },
};

// Colores centralizados para badges de estado en Content Generator
export const statusBadgeColors = {
  draft: "blue",
  review: "yellow",
  accepted: "green",
};

// Configuraciones de colores para incidencias
export const incidentColors = {
  status: {
    new: {
      background: "#fecaca",
      text: "#991b1b",
      border: "#fca5a5",
      className: "bg-red-100 text-red-800 border-red-200",
    },
    open: {
      background: "#fef3c7",
      text: "#92400e",
      border: "#fde68a",
      className: "bg-yellow-100 text-yellow-800 border-yellow-200",
    },
    closed: {
      background: "#E3F6EA",
      text: "#0F9D58",
      border: "#C4E8D2",
      className: "bg-green-100 text-green-800 border-green-200",
    },
  },
};

// Configuraciones de colores para estados de proyectos
export const projectStatusColors = {
  completed: {
    background: "#E3F6EA",
    text: "#0F9D58",
    border: "#C4E8D2",
  },
  in_progress: {
    background: "#fef3c7",
    text: "#92400e",
    border: "#fde68a",
  },
  not_started: {
    background: "#e0e7ff",
    text: "#3730a3",
    border: "#c7d2fe",
  },
  cancelled: {
    background: "#fee2e2",
    text: "#991b1b",
    border: "#fecaca",
  },
};

// Colores centralizados para badges de tipo de plantilla en Project Templates
export const templateTypeBadgeColors = {
  setup: {
    label: "Setup",
    background: brand.soft,
    text: brand.pinkDark,
    border: brand.softBorder,
  },
  recurrent_monthly: {
    label: "Recurrente",
    background: badgeColors.status.pending.background,
    text: badgeColors.status.pending.text,
    border: badgeColors.status.pending.border,
  },
  global_custom: {
    label: "Global",
    background: projectStatusColors.not_started.background,
    text: projectStatusColors.not_started.text,
    border: projectStatusColors.not_started.border,
  },
  agency_custom: {
    label: "Personalizada",
    background: "#f3f4f6",
    text: "#4b5563",
    border: "#e5e7eb",
  },
};

// Variables de datos mock locales para reemplazar AppContext
export const mockDataConfig = {
  agencies: [
    { id: "agency-1", name: "Markezy Madrid" },
    { id: "agency-2", name: "Markezy Barcelona" },
    { id: "agency-3", name: "Markezy Valencia" },
  ],
  consultants: [
    { id: "cons-1", name: "María García", agencyId: "agency-1" },
    { id: "cons-2", name: "Carlos López", agencyId: "agency-1" },
    { id: "cons-3", name: "Ana Martín", agencyId: "agency-2" },
    { id: "cons-4", name: "Diego Fernández", agencyId: "agency-2" },
    { id: "cons-5", name: "Sofia Ruiz", agencyId: "agency-3" },
  ],
  initialClientAssignments: {
    1: { agencyId: "agency-1", consultantId: "cons-1" },
    2: { agencyId: "agency-2", consultantId: "cons-3" },
    3: { consultantId: "cons-2" },
    4: {},
    5: { agencyId: "agency-3", consultantId: "cons-5" },
  },
};

// Exportar también los colores y utilidades para uso directo
export { brand, colors, neutrals, spacing, roleBranding };

// Colores para gráficos y componentes específicos
// Colores específicos para la vista Kanban de clientify
export const kanbanColors = {
  // Fondo común de las columnas de todos los tableros kanban
  columnSurface: "#F4F2F4",
  avatar: {
    background: "rgb(189, 189, 189)",
    text: "#424242",
  },
  columns: {
    contacto_inicial: {
      color: "#f97316",
      bgColor: "#fff7ed",
    },
    reunion_preventa: {
      color: "#3b82f6",
      bgColor: "#eff6ff",
    },
    envio_presupuesto: {
      color: "#8b5cf6",
      bgColor: "#f5f3ff",
    },
    conversion_cliente: {
      color: "#06b6d4",
      bgColor: "#ecfeff",
    },
    ganada: {
      color: "#0F9D58",
      bgColor: "#f0fdf4",
    },
    perdida: {
      color: "#ef4444",
      bgColor: "#fef2f2",
    },
  },
};

export const clientifyTableColors = {
  calendar: {
    pending: colors.success.main,
  },
  statusBadges: colors.clientifyTable.statusBadges,
};

// ---------------------------------------------------------------------------
// Marca blanca por agencia
// ---------------------------------------------------------------------------

// Solo los objetos que contienen colores de marca
const brandableColorTrees = snapshotColorTree([
  colors, roleBranding, notificationTypeColors, lightTheme, darkTheme, platformColors,
  templateTypeBadgeColors,
]);

const applyBrandCssVars = (palette) => {
  const vars = {
    "--primary": palette?.tokens.pink,
    "--primary-foreground": palette?.primaryContrastText,
    "--sidebar-primary": palette?.tokens.pink,
    "--sidebar-ring": palette?.tokens.pink,
    "--chart-1": palette?.tokens.pink,
    "--chart-2": palette?.tokens.orange,
  };
  const { style } = document.documentElement;
  Object.entries(vars).forEach(([name, value]) =>
    value ? style.setProperty(name, value) : style.removeProperty(name)
  );
};

let appliedBrandingKey = brandingKey(null);

export const getAppliedBrandingKey = () => appliedBrandingKey;

/**
 * Recolorea en sitio todos los objetos de color exportados y regenera el theme MUI.
 * Los componentes que leen `brand`/`colors` en render cogen los valores nuevos al
 * volver a montarse (ver BrandingProvider).
 * @param {{primary_color?: string|null, secondary_color?: string|null}|null} branding
 */
export const applyBranding = (branding) => {
  const palette = deriveBrandPalette(branding || {});
  recolorTree(brandableColorTrees, buildReplacer(palette));
  if (palette) {
    brand.ink = palette.ink;
    brand.inkFill = palette.inkFill;
    brand.inkFillText = palette.inkFillText;
    brand.inkHover = palette.inkHover;
    brand.orangeText = palette.orangeText;
    brand.avatar = palette.avatar;
    // Con marca propia los gradientes pasan a color sólido primario
    brand.gradient = palette.tokens.pink;
    brand.gradientHover = palette.tokens.pinkDark;
    brand.gradientDiagonal = palette.tokens.pink;
    brandRolePalette.gradient = palette.tokens.pink;
    colors.betaBadgeGradient = palette.tokens.pink;
    brand.mediaSurface = palette.mediaSurface;
    brand.mediaBorder = palette.mediaBorder;
    colors.primary.contrastText = palette.primaryContrastText;
    brandRolePalette.contrastText = palette.primaryContrastText;
  }
  applyBrandCssVars(palette);
  appliedBrandingKey = brandingKey(branding);
  theme = createAppTheme("light");
  return theme;
};

// Aplicar la marca cacheada antes del primer render (evita parpadeo y cubre
// los estilos que otros módulos calculan al importarse).
let cachedBranding = null;
try {
  cachedBranding = JSON.parse(localStorage.getItem("markezy_auth") || "null")?.user?.agencyBranding || null;
} catch {
  // Sin caché válida: marca por defecto
}
applyBranding(cachedBranding);
